import Select, {
  type FormatOptionLabelMeta,
  type MultiValue,
} from "react-select";
import type {
  AiFunction,
  AiFunctionCatalogItem,
} from "../../types/tenant";

export type AiFunctionOption = {
  value: AiFunction;
  label: string;
  description: string;
};

type AiFunctionsFieldProps = {
  options: AiFunctionOption[];
  value: unknown;
  onChange: (functions: AiFunction[]) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
};

export const normalizeAiFunctions = (
  rawValues: unknown,
  availableFunctions: AiFunction[] = [],
): AiFunction[] => {
  if (!Array.isArray(rawValues)) return [...availableFunctions];

  const seen = new Set<AiFunction>();
  const normalizedValues: AiFunction[] = [];
  const availableFunctionsSet = new Set(availableFunctions);

  rawValues.forEach((value) => {
    if (typeof value !== "string") return;
    if (
      availableFunctionsSet.size > 0 &&
      !availableFunctionsSet.has(value as AiFunction)
    ) {
      return;
    }

    const aiFunction = value as AiFunction;
    if (seen.has(aiFunction)) return;
    seen.add(aiFunction);
    normalizedValues.push(aiFunction);
  });

  return normalizedValues;
};

export const normalizeAiFunctionOptions = (
  items: unknown,
): AiFunctionOption[] => {
  if (!Array.isArray(items)) return [];

  const seen = new Set<string>();
  const options: AiFunctionOption[] = [];

  items.forEach((item) => {
    if (!item || typeof item !== "object") return;
    const record = item as Partial<AiFunctionCatalogItem>;
    const name = typeof record.name === "string" ? record.name.trim() : "";
    if (!name || seen.has(name)) return;

    seen.add(name);
    options.push({
      value: name as AiFunction,
      label:
        typeof record.label === "string" && record.label.trim().length > 0
          ? record.label.trim()
          : name,
      description:
        typeof record.description === "string" ? record.description.trim() : "",
    });
  });

  return options;
};

const formatAiFunctionOptionLabel = (
  option: AiFunctionOption,
  meta: FormatOptionLabelMeta<AiFunctionOption>,
) => (
  <div title={option.description || option.label} className="min-w-0">
    <span className="block truncate">{option.label}</span>
    {meta.context === "menu" && option.description && (
      <span className="mt-0.5 block whitespace-normal text-xs leading-snug text-slate-500">
        {option.description}
      </span>
    )}
  </div>
);

export function AiFunctionsField({
  options,
  value,
  onChange,
  isLoading = false,
  isDisabled = false,
}: AiFunctionsFieldProps) {
  const allAiFunctions = options.map((option) => option.value);
  const selectedAiFunctions = normalizeAiFunctions(value, allAiFunctions);
  const selectedOptions = options.filter((option) =>
    selectedAiFunctions.includes(option.value),
  );

  const handleChange = (selectedOptions: MultiValue<AiFunctionOption>) => {
    const selectedValues = selectedOptions.map((option) => option.value);
    onChange(normalizeAiFunctions(selectedValues, allAiFunctions));
  };

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <label className="form-label mb-0">AI Functions</label>
          <p className="text-xs text-slate-500">
            Selecione quais funções a IA pode chamar nesta conversa.
          </p>
        </div>
        <p className="text-xs text-slate-600">
          Selecionadas: {selectedAiFunctions.length} de {allAiFunctions.length}
        </p>
      </div>

      <div>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <label className="form-label mb-0">Funções disponíveis</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onChange([...allAiFunctions])}
              disabled={isLoading || isDisabled || allAiFunctions.length === 0}
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Todas
            </button>
            <button
              type="button"
              onClick={() => onChange([])}
              disabled={isLoading || isDisabled}
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Nenhuma
            </button>
          </div>
        </div>
        <Select<AiFunctionOption, true>
          isMulti
          closeMenuOnSelect={false}
          options={options}
          value={selectedOptions}
          onChange={handleChange}
          formatOptionLabel={formatAiFunctionOptionLabel}
          isLoading={isLoading}
          isDisabled={isLoading || isDisabled}
          classNamePrefix="ai-functions-select"
          className="text-sm"
          placeholder="Selecione as funções..."
          loadingMessage={() => "Carregando funções..."}
          noOptionsMessage={() => "Nenhuma função encontrada"}
        />
        <p className="mt-1 text-xs text-slate-500">
          Você pode selecionar uma, várias, nenhuma ou todas as funções.
        </p>
      </div>
    </div>
  );
}
